import { isZeroHlc } from '@wiztime/core';
import { queries, type Store } from '@wiztime/storage';
import {
  createMemoryLastSpaceStore,
  createStorageConfigStore,
  createStorageLastSpaceStore,
  markFullPushPending,
  newSyncConfig,
  type LastSpaceStore,
  type SyncConfig,
  type SyncConfigStore,
} from './config';
import {
  newAccountKdf,
  unwrapRecoveryCode,
  wrapRecoveryCode,
  type AccountKdfParams,
  type LoginKeys,
} from './account-keys';
import {
  AccountTransport,
  SessionRejectedError,
  type AccountSessionInfo,
  type LoginResult,
  type VerifyEmailResult,
} from './account-transport';
import { ForeignLocalDataDecision, ForeignLocalDataReason, SyncMode } from './constants';
import {
  deriveKeysInWorker,
  deriveKeysWithRecoveryProofInWorker,
  deriveLoginKeysInWorker,
  type DerivedKeysWithRecoveryProof,
} from './derive-in-worker';
import { createSyncEngine, withSyncLock, type EventSource, type SyncEngine, type SyncNowOptions } from './engine';
import {
  AccountSpaceMismatchError,
  AlreadyAccountModeError,
  ForeignLocalDataCancelledError,
  InsecureServerUrlError,
  InvalidServerUrlError,
  NotAccountModeError,
  PassphraseMismatchError,
  PassphraseSpacesDisabledError,
  RegistrationNotConfirmedError,
  SpaceNotFoundError,
  SyncAlreadyEnabledError,
  SyncHttpError,
  SyncNotEnabledError,
  ServerErrorCode,
} from './errors';
import type { DerivedKeys } from './keys';
import { createIndexedDbKeyStore, type KeyStore } from './key-store';
import { generatePassphrase, requireValidPassphrase } from './passphrase';
import {
  silentLogger,
  SyncErrorCode,
  SyncState,
  type SyncLogger,
  type SyncRunOutcome,
  type SyncStatus,
  type SyncStatusListener,
} from './status';
import { HttpTransport, normalizeBaseUrl } from './transport';

/** What the decision callback learns about the data on this device. */
export interface ForeignLocalDataInfo {
  /** Records the user wrote or received on this device (generated defaults are not counted). */
  readonly localRecords: number;
  /** Why the data counts as foreign: another space was synced here, or its origin is unknown. */
  readonly reason: ForeignLocalDataReason;
}

export interface SyncManagerOptions {
  readonly store: Store;
  /** Defaults to `localStorage` under `wiztime.sync`. */
  readonly configStore?: SyncConfigStore;
  /**
   * The space this device last synced with. Defaults to `localStorage` under `wiztime.lastSpaceId`, or to a
   * memory store when a `configStore` is given (so tests never touch the real storage).
   */
  readonly lastSpaceStore?: LastSpaceStore;
  /**
   * Asked when sync is about to be activated for a space other than the one this device last synced with
   * while the device holds data. `clear` deletes the local data first, `merge` uploads it to the new space,
   * `cancel` aborts with `ForeignLocalDataCancelledError`. Without this callback the activation is cancelled.
   */
  readonly onForeignLocalData?: (info: ForeignLocalDataInfo) => Promise<ForeignLocalDataDecision>;
  /** Called after the manager cleared the local data, so the app can reset what depends on it (first-run flag, hints). */
  readonly onLocalDataCleared?: () => void;
  /**
   * Called once sync is activated on this device (after registering, logging in, joining or enabling with a
   * passphrase, once the configuration is saved and the engine is attached). The app uses it to leave local mode.
   */
  readonly onSyncActivated?: () => void;
  /** Defaults to the IndexedDB database `wiztime-keys`. */
  readonly keyStore?: KeyStore;
  readonly fetch?: typeof fetch;
  /** Derives the keys of a normalised passphrase; defaults to Argon2 in a Web Worker. */
  readonly deriveKeys?: (passphrase: string) => Promise<DerivedKeys>;
  /** Like `deriveKeys`, and also the recovery proof (registration, upgrade, password reset). */
  readonly deriveKeysWithRecoveryProof?: (passphrase: string) => Promise<DerivedKeysWithRecoveryProof>;
  /**
   * Derives the login proof and wrap key from the login password and range-checked KDF parameters;
   * defaults to Argon2 in a Web Worker. Tests may lower the Argon2 cost here (the parameters sent to the
   * server stay in range).
   */
  readonly deriveLoginKeys?: (password: string, params: AccountKdfParams) => Promise<LoginKeys>;
  /** Language tag of the user interface at call time; sent so that the server mails in the user's language. */
  readonly locale?: () => string | undefined;
  readonly now?: () => number;
  readonly logger?: SyncLogger;
  readonly random?: () => number;
  readonly lock?: <T>(task: () => Promise<T>) => Promise<T>;
  readonly events?: EventSource | null;
  readonly onRemoteApplied?: (changedIds: readonly string[]) => void;
  readonly debounceMs?: number;
  /** Records requested per pull page (see `SyncEngineOptions.pullPageSize`); tests only. */
  readonly pullPageSize?: number;
  readonly intervalMs?: number;
  readonly requestTimeoutMs?: number;
}

export interface EnableSyncInput {
  /** Server origin such as `https://sync.example.com`; empty for the origin serving the page. */
  readonly serverUrl: string;
  /** The 12-word passphrase (typically from `generatePassphrase`). */
  readonly passphrase: string;
}

/** What the settings screen shows about a configured sync. */
export interface SyncSettings {
  readonly serverUrl: string;
  /** First 8 characters of the space identifier, for display and diagnostics. */
  readonly spaceIdPrefix: string;
  readonly lastSyncAt: number | null;
  readonly mode: SyncMode;
  /** Email of the logged-in account; '' in passphrase mode. */
  readonly accountEmail: string;
}

export interface RegisterInput {
  readonly serverUrl: string;
  readonly email: string;
  /** The login password. */
  readonly password: string;
}

export interface RegisterResult {
  /** The generated 12-word recovery code; show it to the user, who must save it. */
  readonly recoveryCode: string;
}

export interface LoginInput {
  readonly serverUrl: string;
  readonly email: string;
  readonly password: string;
}

export interface ResetPasswordInput {
  readonly serverUrl: string;
  readonly email: string;
  /** The 12-word recovery code. */
  readonly recoveryCode: string;
  readonly newPassword: string;
}

export interface UpgradeToAccountInput {
  readonly email: string;
  readonly password: string;
  /** The sync passphrase of the space this device syncs; it becomes the account's recovery code. */
  readonly passphrase: string;
}

export interface VerifyEmailInput {
  /** Server that issued the link; empty for the origin serving the page. */
  readonly serverUrl: string;
  readonly token: string;
}

/** Choices of leaving a space on this device (`logout`, `disableSync`). */
export interface LeaveOptions {
  /**
   * Also delete the local data once sync is off, so that the next person at this browser, or the next account,
   * does not find it. Data not yet uploaded is lost: check `countUnpushedRecords` first.
   */
  readonly clearLocalData?: boolean;
}

export interface AccountSummary {
  readonly email: string;
  /** False until the verification link was opened (also on servers that never ask for it). */
  readonly emailVerified: boolean;
  /**
   * True when the server refuses uploads until the email is verified: the account is unverified and the server
   * says it requires verification (`verificationRequired` of the account answer; when the server does not send
   * it, whether the last sync was refused with `email_unverified`). False on servers with
   * `WIZTIME_EMAIL_VERIFICATION=off`.
   */
  readonly verificationRequired: boolean;
}

export interface ChangeEmailInput {
  /** The login password (confirms the request). */
  readonly password: string;
  readonly newEmail: string;
}

/** The sync feature as the settings UI sees it: configuration, lifecycle and status. */
export interface SyncManager {
  /** Resume sync on app start when this device was set up earlier. */
  start(): Promise<void>;
  /** Stop syncing without forgetting the configuration (app shutdown, tests). */
  stop(): Promise<void>;
  getStatus(): SyncStatus;
  subscribe(listener: SyncStatusListener): () => void;
  /** `null` while sync is not configured. */
  getSettings(): SyncSettings | null;
  /**
   * Turn sync on with a new passphrase: creates the space on the server (or joins it if it
   * already exists). Resolves with whether the space was newly created. Rejects with
   * `InvalidPassphraseError`, `InvalidServerUrlError`, `SyncAlreadyEnabledError`, `SyncHttpError` or `SyncNetworkError`.
   */
  enableSync(input: EnableSyncInput): Promise<{ readonly created: boolean }>;
  /**
   * Turn sync on with the passphrase of an existing space, typically from another device. Unlike
   * `enableSync` it never creates a space: when none exists it rejects with `SpaceNotFoundError`
   * (a mistyped passphrase can still pass the checksum with probability 1/16).
   */
  joinSync(input: EnableSyncInput): Promise<void>;
  /**
   * Create an account with a new space and start syncing as that account. Resolves with the
   * recovery code. Rejects with `InvalidServerUrlError`, `SyncAlreadyEnabledError`,
   * `AccountsUnsupportedError`, `RegistrationClosedError`, `EmailTakenError`, `SyncHttpError`, `SyncNetworkError`,
   * `RegistrationNotConfirmedError` or `ForeignLocalDataCancelledError`.
   *
   * Sync is only activated when the login right after registration succeeds for the registered space. A server
   * that hides registered emails answers a registration of a taken email like a success; the login then fails
   * and `RegistrationNotConfirmedError` is thrown with nothing written on this device. Failure after the server
   * created the account (local key storage fails, say): nothing is written on this device and the error is
   * thrown; the account exists, the user can `login` with the password, and the recovery code stays
   * recoverable from the wrapped copy on the server.
   */
  register(input: RegisterInput): Promise<RegisterResult>;
  /**
   * Log in to an existing account on this device and start syncing its space (the account's recovery code
   * is unwrapped with the password and goes through the same path as `joinSync`). Rejects with
   * `KdfParamsRejectedError` (unsafe server parameters), `InvalidCredentialsError`, `UnwrapFailedError`,
   * `AccountsUnsupportedError`, `SyncAlreadyEnabledError`, `ForeignLocalDataCancelledError` and the network errors. When the account's
   * space was deleted on the server, login succeeds and sync reports `SyncState.SpaceMissing`.
   */
  login(input: LoginInput): Promise<void>;
  /**
   * Set a new login password using the recovery code ("forgot password"). Does not touch the sync state
   * of this device and opens no session; call `login` afterwards. Rejects with `InvalidPassphraseError`
   * (malformed recovery code), `InvalidRecoveryCodeError`, `AccountsUnsupportedError` and the network errors.
   */
  resetPassword(input: ResetPasswordInput): Promise<void>;
  /**
   * Log out of the account on this device: ends the server session (best effort) and then behaves like
   * `disableSync`: sync stops and the keys and configuration are forgotten, local data stays. A browser
   * without keys cannot sync, and keeping them after "log out" would leave the data readable by the next
   * person at this browser. Rejects with `SyncNotEnabledError` or `NotAccountModeError`.
   */
  logout(options?: LeaveOptions): Promise<void>;
  /**
   * Delete the account, its space and all records on the server, then turn sync off on this device (local
   * data stays). Needs the login password. Rejects with `InvalidCredentialsError` (nothing changes),
   * `SyncNotEnabledError`, `NotAccountModeError` and the network errors.
   */
  deleteAccount(input: { readonly password: string }): Promise<void>;
  /**
   * Change the login password. Needs the current password (verified by the server). The recovery code is
   * unwrapped with the current password and wrapped again with the new one; it does not change. Opens a
   * fresh session for the call, which then replaces this device's stored session; the server ends every
   * other session of the account, so other devices must log in again for account management (their sync
   * keeps working: it uses the write token). Rejects with `InvalidCredentialsError` (nothing changes),
   * `KdfParamsRejectedError`, `UnwrapFailedError`, `AccountSpaceMismatchError`, `SyncNotEnabledError`,
   * `NotAccountModeError` and the network errors.
   */
  changePassword(input: { readonly currentPassword: string; readonly newPassword: string }): Promise<void>;
  /**
   * Show the recovery code: unwraps it with the login password. The server has no endpoint that returns the
   * wrapped code without a login, so this opens a session and ends it again right away (best effort); the
   * stored session of this device is not touched and nothing else is written. Rejects with
   * `InvalidCredentialsError`, `KdfParamsRejectedError`, `UnwrapFailedError`, `AccountSpaceMismatchError`,
   * `SyncNotEnabledError`, `NotAccountModeError` and the network errors.
   */
  revealRecoveryCode(input: { readonly password: string }): Promise<string>;
  /**
   * Link the space this device already syncs with a passphrase to a new account. No data is uploaded again:
   * the cursor and push watermarks stay. Rejects with `SyncNotEnabledError`, `AlreadyAccountModeError`,
   * `PassphraseMismatchError`, `EmailTakenError`, `SpaceTakenError`, `AccountsUnsupportedError`,
   * `RegistrationNotConfirmedError` (the login after registering failed: the mode is not changed) and the network errors.
   */
  upgradeToAccount(input: UpgradeToAccountInput): Promise<void>;
  /**
   * Apply the token of a verification or email-change link. Needs no session and no sync configuration.
   * When this device is logged in to an account on that server, it then refreshes the stored email (after a
   * change of email) and syncs at once, which uploads the data held back while the email was unverified.
   * Rejects with `VerificationLinkInvalidError`, `EmailTakenError` (the new email was taken meanwhile),
   * `InvalidServerUrlError`, `AccountsUnsupportedError` and the network errors.
   */
  verifyEmail(input: VerifyEmailInput): Promise<VerifyEmailResult>;
  /**
   * The account's email, whether it is verified and whether the server requires that, read with the stored session; also repairs a stored email
   * that was changed from another device. Rejects with `SessionRejectedError` (no usable session: call
   * `renewSession`), `SyncNotEnabledError`, `NotAccountModeError` and the network errors.
   */
  accountInfo(): Promise<AccountSummary>;
  /** Send the verification mail again. Rejects with `SessionRejectedError`, `SyncHttpError` (429: too many mails) and the others of `accountInfo`. */
  resendVerification(): Promise<void>;
  /**
   * Ask to change the login email; the new address gets a link and the change takes effect when it is opened.
   * Rejects with `InvalidCredentialsError` (wrong password), `EmailTakenError`, `SyncHttpError` (400 invalid
   * address, 429 too many mails), `KdfParamsRejectedError`, `SyncNotEnabledError`, `NotAccountModeError` and the
   * network errors. Logs in again when the stored session is missing or revoked.
   */
  changeEmail(input: ChangeEmailInput): Promise<void>;
  /** Live login sessions of the account. Rejects like `accountInfo`. */
  listSessions(): Promise<AccountSessionInfo[]>;
  /** End one session of the account (not this device's own). Rejects like `accountInfo`; 404 `SyncHttpError`: already gone. */
  revokeSession(id: string): Promise<void>;
  /**
   * Open a new account session with the password and store it, for a device whose session was revoked or
   * never opened. Sync is not affected. Rejects with `InvalidCredentialsError`, `KdfParamsRejectedError` and the others.
   */
  renewSession(input: { readonly password: string }): Promise<void>;
  /**
   * Stop syncing and forget the keys and the configuration; server data stays, local data stays unless
   * `clearLocalData` is set. An account session of this device is ended on the server (best effort).
   */
  disableSync(options?: LeaveOptions): Promise<void>;
  /** Delete the space and all its records on the server, then turn sync off on this device (ending an account session). */
  deleteRemoteData(): Promise<void>;
  /**
   * How many local records are not on the server yet (the outbox of the running configuration): what a
   * logout with `clearLocalData` would lose. 0 while sync is not configured.
   */
  countUnpushedRecords(): Promise<number>;
  syncNow(options?: SyncNowOptions): Promise<SyncRunOutcome>;
  /**
   * Answer the `SyncState.SpaceMissing` state ("the cloud data was deleted"): create the space on
   * the server again and upload every local record. Resolves with the outcome of that sync;
   * `{ ok: false, state: Disabled }` while sync is not running.
   */
  reuploadSpace(): Promise<SyncRunOutcome>;
  notifyLocalChange(): void;
  /**
   * Push every local record at the next sync (call after importing an export file). The request is
   * stored in the configuration, so it also holds while no sync is running.
   */
  requestFullPush(): void;
}

const DISABLED: SyncStatus = {
  state: SyncState.Disabled,
  lastSyncAt: null,
  lastError: null,
  skippedRecords: 0,
  retryAt: null,
};

/** This machine: browsers treat it as a secure context and allow `http:` requests to it from an `https:` page. */
function isLoopbackHost(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '[::1]' || /^127(\.\d{1,3}){3}$/.test(hostname);
}

/** Whether `url` (as accepted by `requireServerUrl`) sends the access token unencrypted: `http:` to another machine. */
export function isInsecureServerUrl(url: string): boolean {
  if (url === '') return false;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' && !isLoopbackHost(parsed.hostname);
  } catch {
    return false;
  }
}

/**
 * Empty (same origin) or an http(s) URL without trailing slash. An `http:` address is refused
 * (`InsecureServerUrlError`) when the page itself is served over `https:`, unless it points at this
 * machine: the browser would block the request as mixed content and the user would only see a
 * network failure. `pageProtocol` is the protocol of the page (`location.protocol`; undefined
 * outside a browser).
 */
export function requireServerUrl(
  input: string,
  pageProtocol: string | undefined = globalThis.location?.protocol,
): string {
  const trimmed = normalizeBaseUrl(input);
  if (trimmed === '') return '';
  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    throw new InvalidServerUrlError();
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') throw new InvalidServerUrlError();
  if (parsed.protocol === 'http:' && pageProtocol === 'https:' && !isLoopbackHost(parsed.hostname)) {
    throw new InsecureServerUrlError();
  }
  return trimmed;
}

/** Whether two server addresses (empty: the origin serving the page) are the same origin. */
function sameServer(a: string, b: string): boolean {
  const origin = (url: string): string => (url === '' ? (globalThis.location?.origin ?? '') : url);
  return origin(a) === origin(b);
}

/** The form the server compares emails in: trimmed, Unicode NFC, lower case. */
export function normalizeEmail(email: string): string {
  return email.trim().normalize('NFC').toLowerCase();
}

export function createSyncManager(options: SyncManagerOptions): SyncManager {
  const { store } = options;
  const configStore = options.configStore ?? createStorageConfigStore();
  const keyStore = options.keyStore ?? createIndexedDbKeyStore();
  const lastSpaceStore =
    options.lastSpaceStore ??
    (options.configStore === undefined ? createStorageLastSpaceStore() : createMemoryLastSpaceStore());
  const derive = options.deriveKeys ?? ((passphrase: string) => deriveKeysInWorker(passphrase));
  const deriveWithProof =
    options.deriveKeysWithRecoveryProof ??
    ((passphrase: string) => deriveKeysWithRecoveryProofInWorker(passphrase));
  const deriveLogin =
    options.deriveLoginKeys ?? ((password: string, params: AccountKdfParams) => deriveLoginKeysInWorker(password, params));
  const logger = options.logger ?? silentLogger;

  let status: SyncStatus = DISABLED;
  const listeners = new Set<SyncStatusListener>();
  let engine: SyncEngine | null = null;
  let unsubscribeEngine: (() => void) | null = null;
  let activeKeys: DerivedKeys | null = null;
  /** The `start` in progress: concurrent calls share it instead of each attaching an engine. */
  let starting: Promise<void> | null = null;

  function setStatus(next: SyncStatus): void {
    status = next;
    for (const listener of [...listeners]) listener(status);
  }

  function transportFor(serverUrl: string, keys: DerivedKeys): HttpTransport {
    return new HttpTransport({
      baseUrl: serverUrl,
      spaceId: keys.spaceId,
      authToken: keys.authToken,
      ...(options.fetch === undefined ? {} : { fetch: options.fetch }),
      ...(options.requestTimeoutMs === undefined ? {} : { timeoutMs: options.requestTimeoutMs }),
    });
  }

  /** Start an engine for this configuration; an engine that is still attached is stopped first. */
  async function attach(config: SyncConfig, keys: DerivedKeys): Promise<void> {
    await detach();
    activeKeys = keys;
    if (!lastSpaceStore.save(keys.spaceId)) {
      logger.warn('the last synced space could not be saved; foreign local data is detected by record device ids');
    }
    const next = createSyncEngine({
      store,
      keys,
      transport: transportFor(config.serverUrl, keys),
      storage: configStore,
      logger,
      ...(options.now === undefined ? {} : { now: options.now }),
      ...(options.random === undefined ? {} : { random: options.random }),
      ...(options.lock === undefined ? {} : { lock: options.lock }),
      ...(options.events === undefined ? {} : { events: options.events }),
      ...(options.onRemoteApplied === undefined ? {} : { onRemoteApplied: options.onRemoteApplied }),
      ...(options.debounceMs === undefined ? {} : { debounceMs: options.debounceMs }),
      ...(options.pullPageSize === undefined ? {} : { pullPageSize: options.pullPageSize }),
      ...(options.intervalMs === undefined ? {} : { intervalMs: options.intervalMs }),
    });
    engine = next;
    unsubscribeEngine = next.subscribe(setStatus);
    next.start();
    setStatus(next.getStatus());
  }

  async function detach(): Promise<void> {
    const current = engine;
    unsubscribeEngine?.();
    unsubscribeEngine = null;
    engine = null;
    activeKeys = null;
    await current?.stop();
  }

  function accountTransportFor(serverUrl: string): AccountTransport {
    return new AccountTransport({
      baseUrl: serverUrl,
      ...(options.fetch === undefined ? {} : { fetch: options.fetch }),
      ...(options.requestTimeoutMs === undefined ? {} : { timeoutMs: options.requestTimeoutMs }),
    });
  }

  /** Refuse when sync is configured; validate the address and warn about an unencrypted one. */
  function prepareServerUrl(rawUrl: string): string {
    if (configStore.load() !== null) throw new SyncAlreadyEnabledError();
    const serverUrl = requireServerUrl(rawUrl);
    if (isInsecureServerUrl(serverUrl)) {
      logger.warn('the sync server address is not encrypted (http): the access token can be intercepted', {
        host: new URL(serverUrl).host,
      });
    }
    return serverUrl;
  }

  interface Establishment {
    readonly serverUrl: string;
    readonly keys: DerivedKeys;
    readonly mode: SyncMode;
    readonly accountEmail: string;
    /** The account session token to keep; `null` for the passphrase mode or when none could be opened. */
    readonly session: string | null;
  }

  /** Persist keys and configuration of a new sync and start the engine. Shared by every way of turning sync on. */
  async function activate(
    target: Establishment,
    space: { created: boolean; epoch: string },
  ): Promise<void> {
    // Read before `attach` records the target as the last space.
    const lastSpace = lastSpaceStore.load();
    await keyStore.save(target.keys);
    if (target.session === null) await keyStore.clearSession();
    else await keyStore.saveSession(target.session);
    // An existing space (not created now, known to the server) that this device did not sync with last: read it
    // before writing to it. Returning to the device's own last space is an ordinary sync.
    const pullFirst = !space.created && space.epoch !== '' && lastSpace !== target.keys.spaceId;
    const config = newSyncConfig(target.serverUrl, target.keys.spaceId, {
      epoch: space.epoch,
      dbInstanceId: await store.getDbInstanceId(),
      mode: target.mode,
      accountEmail: target.accountEmail,
      pullFirst,
    });
    configStore.save(config);
    await attach(config, target.keys);
    options.onSyncActivated?.();
    logger.info('sync enabled', {
      space: target.keys.spaceId.slice(0, 8),
      created: space.created,
      mode: target.mode,
    });
  }

  /**
   * Records the user wrote or received on this device (generated ones, with a zero hlc, are rebuilt anywhere),
   * and how many of them were last written by another device: evidence that they came from a sync.
   */
  async function countLocalRecords(): Promise<{ total: number; fromOtherDevices: number }> {
    const records = (await queries.allRecords(store.repo)).filter((record) => !isZeroHlc(record.hlc));
    return {
      total: records.length,
      fromOtherDevices: records.filter((record) => record.deviceId !== store.deviceId).length,
    };
  }

  /** Delete every local record and forget the last space (the same operation as "clear all data" in the settings). */
  async function clearLocalData(): Promise<void> {
    // Under the sync lock: a pull in another tab cannot write records after the clear.
    await (options.lock ?? withSyncLock)(async () => {
      await store.clearAllData();
      lastSpaceStore.clear();
    });
    options.onLocalDataCleared?.();
    logger.info('local data cleared');
  }

  /**
   * Called with the target space after its identifier is known and before anything is written, started or
   * uploaded. Local data that belongs to another space must never flow into this one unasked.
   * - No remembered space and every record written by this device (never synced): continue, the first sync uploads it.
   * - No remembered space but records written by other devices (synced before the space was remembered): ask.
   * - Same space, or an empty device: continue.
   * - Another space: ask. `clear` deletes the local data (the device only pulls), `merge` continues (the
   *   first sync uploads everything), `cancel` throws `ForeignLocalDataCancelledError`.
   */
  async function guardForeignLocalData(spaceId: string): Promise<void> {
    const last = lastSpaceStore.load();
    if (last === spaceId) return;
    const counts = await countLocalRecords();
    const localRecords = counts.total;
    if (localRecords === 0) return;
    // No remembered space: devices that synced before the space was remembered (or whose storage failed) are
    // recognised by records written by other devices; purely local data is never asked about.
    if (last === '' && counts.fromOtherDevices === 0) return;
    const reason = last === '' ? ForeignLocalDataReason.UnknownOrigin : ForeignLocalDataReason.OtherSpace;
    const decide = options.onForeignLocalData;
    const decision = decide === undefined ? ForeignLocalDataDecision.Cancel : await decide({ localRecords, reason });
    if (decision === ForeignLocalDataDecision.Clear) {
      await clearLocalData();
      return;
    }
    if (decision === ForeignLocalDataDecision.Merge) {
      logger.info('local data of another space is merged into this space on request', { localRecords });
      return;
    }
    throw new ForeignLocalDataCancelledError();
  }

  /** Turn sync on from a passphrase (the "establish from passphrase" path of enableSync and joinSync). */
  async function establish(
    input: EnableSyncInput,
    openSpace: (transport: HttpTransport) => Promise<{ created: boolean; epoch: string }>,
  ): Promise<{ created: boolean }> {
    if (configStore.load() !== null) throw new SyncAlreadyEnabledError();
    const passphrase = requireValidPassphrase(input.passphrase);
    const serverUrl = prepareServerUrl(input.serverUrl);
    const keys = await derive(passphrase);
    await guardForeignLocalData(keys.spaceId);
    const space = await openSpace(transportFor(serverUrl, keys));
    await activate(
      { serverUrl, keys, mode: SyncMode.Passphrase, accountEmail: '', session: null },
      space,
    );
    return { created: space.created };
  }

  /** The account's session was opened but turning sync on failed: do not leave the session behind. */
  async function discardSession(serverUrl: string, token: string | null): Promise<void> {
    if (token === null) return;
    try {
      await accountTransportFor(serverUrl).logout(token);
    } catch {
      logger.warn('could not end the account session on the server');
    }
  }

  /**
   * Log in right after registration or upgrade. Only a session for the registered space proves that the account
   * is ours: a server that hides registered emails answers the registration of a taken email like a success.
   */
  async function confirmRegistration(
    transport: AccountTransport,
    serverUrl: string,
    email: string,
    loginProof: string,
    spaceId: string,
  ): Promise<string> {
    let session: LoginResult;
    try {
      session = await transport.login(email, loginProof);
    } catch (error) {
      logger.warn('registration could not be confirmed by logging in', {
        reason: error instanceof Error ? error.name : 'unknown',
      });
      throw new RegistrationNotConfirmedError();
    }
    if (session.spaceId !== spaceId) {
      await discardSession(serverUrl, session.sessionToken);
      logger.warn('registration could not be confirmed: the account belongs to another space');
      throw new RegistrationNotConfirmedError();
    }
    return session.sessionToken;
  }

  /** The login proof of the stored account for the confirmation of dangerous operations. */
  async function proofFor(transport: AccountTransport, email: string, password: string): Promise<string> {
    const kdf = await transport.prelogin(email);
    return (await deriveLogin(password, kdf)).loginProof;
  }

  async function registerAccount(input: RegisterInput): Promise<RegisterResult> {
    const serverUrl = prepareServerUrl(input.serverUrl);
    const email = normalizeEmail(input.email);
    const recoveryCode = generatePassphrase();
    const { keys, recoveryProof } = await deriveWithProof(recoveryCode);
    await guardForeignLocalData(keys.spaceId);
    const kdf = newAccountKdf();
    const { loginProof, wrapKey } = await deriveLogin(input.password, kdf);
    const wrapped = await wrapRecoveryCode(recoveryCode, wrapKey, keys.spaceId);
    const transport = accountTransportFor(serverUrl);
    const space = await transport.register({
      email,
      kdf,
      loginProof,
      wrapped,
      spaceId: keys.spaceId,
      spaceToken: keys.authToken,
      recoveryProof,
      ...localeField(),
    });
    const session = await confirmRegistration(transport, serverUrl, email, loginProof, keys.spaceId);
    try {
      await activate({ serverUrl, keys, mode: SyncMode.Account, accountEmail: email, session }, space);
    } catch (error) {
      await keyStore.clear().catch(() => undefined);
      configStore.clear();
      await discardSession(serverUrl, session);
      throw error;
    }
    return { recoveryCode };
  }

  async function loginAccount(input: LoginInput): Promise<void> {
    const serverUrl = prepareServerUrl(input.serverUrl);
    const email = normalizeEmail(input.email);
    const transport = accountTransportFor(serverUrl);
    const kdf = await transport.prelogin(email);
    // Throws KdfParamsRejectedError for parameters outside the accepted range, before any derivation.
    const { loginProof, wrapKey } = await deriveLogin(input.password, kdf);
    const session = await transport.login(email, loginProof);
    try {
      const recoveryCode = await unwrapRecoveryCode(session.wrapped, wrapKey, session.spaceId);
      const keys = await derive(recoveryCode);
      if (keys.spaceId !== session.spaceId) {
        throw new AccountSpaceMismatchError();
      }
      await guardForeignLocalData(keys.spaceId);
      let space: { created: boolean; epoch: string };
      try {
        const { epoch } = await transportFor(serverUrl, keys).pull(0, 1);
        space = { created: false, epoch };
      } catch (error) {
        if (!(error instanceof SyncHttpError && error.code === ServerErrorCode.NotFound)) throw error;
        // The cloud data was deleted but the account stays: sync starts and reports SpaceMissing.
        space = { created: false, epoch: '' };
      }
      await activate(
        { serverUrl, keys, mode: SyncMode.Account, accountEmail: email, session: session.sessionToken },
        space,
      );
    } catch (error) {
      await keyStore.clear().catch(() => undefined);
      configStore.clear();
      await discardSession(serverUrl, session.sessionToken);
      throw error;
    }
  }

  async function resetAccountPassword(input: ResetPasswordInput): Promise<void> {
    const recoveryCode = requireValidPassphrase(input.recoveryCode);
    const serverUrl = requireServerUrl(input.serverUrl);
    const email = normalizeEmail(input.email);
    const { keys, recoveryProof } = await deriveWithProof(recoveryCode);
    const kdf = newAccountKdf();
    const { loginProof, wrapKey } = await deriveLogin(input.newPassword, kdf);
    const wrapped = await wrapRecoveryCode(recoveryCode, wrapKey, keys.spaceId);
    await accountTransportFor(serverUrl).reset({ email, recoveryProof, kdf, loginProof, wrapped });
    logger.info('account password reset');
  }

  function localeField(): { locale?: string } {
    const locale = options.locale?.();
    return locale === undefined || locale === '' ? {} : { locale };
  }

  /** Configuration of a device that is logged in to an account. */
  function requireAccountConfig(): SyncConfig {
    const config = configStore.load();
    if (config === null) throw new SyncNotEnabledError();
    if (config.mode !== SyncMode.Account) throw new NotAccountModeError();
    return config;
  }

  async function deleteAccountOfDevice(password: string): Promise<void> {
    const config = requireAccountConfig();
    const transport = accountTransportFor(config.serverUrl);
    const loginProof = await proofFor(transport, config.accountEmail, password);
    let token = await keyStore.loadSession();
    let fresh = false;
    if (token === null) {
      token = (await transport.login(config.accountEmail, loginProof)).sessionToken;
      fresh = true;
    }
    try {
      try {
        await transport.deleteAccount(token, loginProof);
      } catch (error) {
        if (fresh || !(error instanceof SessionRejectedError)) throw error;
        // The stored session expired or was revoked (password reset on another device): log in again.
        token = (await transport.login(config.accountEmail, loginProof)).sessionToken;
        fresh = true;
        await transport.deleteAccount(token, loginProof);
      }
    } catch (error) {
      if (fresh) await discardSession(config.serverUrl, token);
      throw error;
    }
    await disableLocally(false);
    logger.info('account deleted and sync disabled');
  }

  /** Log in with the current password and open the wrapped recovery code (shared by change and reveal). */
  async function openRecoveryCode(
    config: SyncConfig,
    transport: AccountTransport,
    password: string,
  ): Promise<{ session: LoginResult; recoveryCode: string; loginProof: string }> {
    const kdf = await transport.prelogin(config.accountEmail);
    const { loginProof, wrapKey } = await deriveLogin(password, kdf);
    const session = await transport.login(config.accountEmail, loginProof);
    try {
      if (session.spaceId !== config.spaceId) throw new AccountSpaceMismatchError();
      const recoveryCode = await unwrapRecoveryCode(session.wrapped, wrapKey, config.spaceId);
      return { session, recoveryCode, loginProof };
    } catch (error) {
      await discardSession(config.serverUrl, session.sessionToken);
      throw error;
    }
  }

  async function changeAccountPassword(input: {
    currentPassword: string;
    newPassword: string;
  }): Promise<void> {
    const config = requireAccountConfig();
    const transport = accountTransportFor(config.serverUrl);
    const {
      session,
      recoveryCode,
      loginProof: currentProof,
    } = await openRecoveryCode(config, transport, input.currentPassword);
    try {
      const kdf = newAccountKdf();
      const { loginProof, wrapKey } = await deriveLogin(input.newPassword, kdf);
      const wrapped = await wrapRecoveryCode(recoveryCode, wrapKey, config.spaceId);
      await transport.changePassword(session.sessionToken, {
        currentLoginProof: currentProof,
        kdf,
        loginProof,
        wrapped,
      });
    } catch (error) {
      await discardSession(config.serverUrl, session.sessionToken);
      throw error;
    }
    // The server ended every other session, including the one stored so far; the new one replaces it.
    await keyStore.saveSession(session.sessionToken);
    logger.info('account password changed');
  }

  async function revealCode(input: { password: string }): Promise<string> {
    const config = requireAccountConfig();
    const transport = accountTransportFor(config.serverUrl);
    const { session, recoveryCode } = await openRecoveryCode(config, transport, input.password);
    await discardSession(config.serverUrl, session.sessionToken);
    return recoveryCode;
  }

  async function upgrade(input: UpgradeToAccountInput): Promise<void> {
    const config = configStore.load();
    if (config === null) throw new SyncNotEnabledError();
    if (config.mode === SyncMode.Account) throw new AlreadyAccountModeError();
    const passphrase = requireValidPassphrase(input.passphrase);
    const email = normalizeEmail(input.email);
    const { keys, recoveryProof } = await deriveWithProof(passphrase);
    if (keys.spaceId !== config.spaceId) throw new PassphraseMismatchError();
    await guardForeignLocalData(keys.spaceId);
    const kdf = newAccountKdf();
    const { loginProof, wrapKey } = await deriveLogin(input.password, kdf);
    const wrapped = await wrapRecoveryCode(passphrase, wrapKey, keys.spaceId);
    const transport = accountTransportFor(config.serverUrl);
    await transport.register({
      email,
      kdf,
      loginProof,
      wrapped,
      spaceId: keys.spaceId,
      spaceToken: keys.authToken,
      recoveryProof,
      ...localeField(),
    });
    const session = await confirmRegistration(transport, config.serverUrl, email, loginProof, keys.spaceId);
    try {
      await keyStore.saveSession(session);
    } catch (error) {
      await discardSession(config.serverUrl, session);
      throw error;
    }
    // Only the mode changes; cursor, watermarks and epoch stay, so nothing is uploaded again. Written
    // through a fresh read because the running engine updates the same entry.
    const current = configStore.load();
    if (current !== null) configStore.save({ ...current, mode: SyncMode.Account, accountEmail: email });
    logger.info('sync upgraded to an account', { space: keys.spaceId.slice(0, 8) });
  }

  /** Run `call` with the stored session; without one, or when the server rejects it, `SessionRejectedError`. */
  async function withStoredSession<T>(
    call: (transport: AccountTransport, token: string, config: SyncConfig) => Promise<T>,
  ): Promise<T> {
    const config = requireAccountConfig();
    const token = await keyStore.loadSession();
    if (token === null) throw new SessionRejectedError(401, 'This device has no account session');
    return call(accountTransportFor(config.serverUrl), token, config);
  }

  async function readAccountInfo(): Promise<AccountSummary> {
    return withStoredSession(async (transport, token, config) => {
      const info = await transport.me(token);
      if (info.email !== config.accountEmail) {
        // The email was changed from another device. Written through a fresh read: the engine updates the same entry.
        const current = configStore.load();
        if (current !== null) configStore.save({ ...current, accountEmail: info.email });
      }
      return {
        email: info.email,
        emailVerified: info.emailVerified,
        // Older servers do not send the field: then the refusal of the last sync tells.
        verificationRequired: info.emailVerified
          ? false
          : (info.verificationRequired ?? status.lastError === SyncErrorCode.EmailUnverified),
      };
    });
  }

  async function verifyEmailLink(input: VerifyEmailInput): Promise<VerifyEmailResult> {
    const serverUrl = requireServerUrl(input.serverUrl);
    const result = await accountTransportFor(serverUrl).verifyEmail(input.token);
    // On a freshly opened page the engine may still be starting; the sync below needs it.
    await starting?.catch(() => undefined);
    const config = configStore.load();
    if (config !== null && config.mode === SyncMode.Account && sameServer(config.serverUrl, serverUrl)) {
      // Best effort: the link is applied whatever happens here.
      if (result.emailChanged) await readAccountInfo().catch(() => undefined);
      void engine?.syncNow({}).catch(() => undefined);
    }
    return result;
  }

  async function changeEmailOfAccount(input: ChangeEmailInput): Promise<void> {
    const config = requireAccountConfig();
    const transport = accountTransportFor(config.serverUrl);
    const loginProof = await proofFor(transport, config.accountEmail, input.password);
    const request = { loginProof, newEmail: input.newEmail.trim(), ...localeField() };
    let token = await keyStore.loadSession();
    let fresh = false;
    if (token === null) {
      token = (await transport.login(config.accountEmail, loginProof)).sessionToken;
      fresh = true;
    }
    try {
      try {
        await transport.changeEmail(token, request);
      } catch (error) {
        if (fresh || !(error instanceof SessionRejectedError)) throw error;
        token = (await transport.login(config.accountEmail, loginProof)).sessionToken;
        fresh = true;
        await transport.changeEmail(token, request);
      }
    } catch (error) {
      if (fresh) await discardSession(config.serverUrl, token);
      throw error;
    }
    // The device had no usable session: keep the new one, it is the same as after a login.
    if (fresh) await keyStore.saveSession(token);
    logger.info('email change requested');
  }

  async function renewAccountSession(input: { password: string }): Promise<void> {
    const config = requireAccountConfig();
    const transport = accountTransportFor(config.serverUrl);
    const loginProof = await proofFor(transport, config.accountEmail, input.password);
    const session = await transport.login(config.accountEmail, loginProof);
    if (session.spaceId !== config.spaceId) {
      await discardSession(config.serverUrl, session.sessionToken);
      throw new AccountSpaceMismatchError();
    }
    const previous = await keyStore.loadSession();
    await keyStore.saveSession(session.sessionToken);
    if (previous !== null) await discardSession(config.serverUrl, previous);
  }

  /** Ask the server to end the account session of this device; a failure only means it expires by itself. */
  async function endAccountSession(config: SyncConfig | null): Promise<void> {
    if (config === null || config.mode !== SyncMode.Account) return;
    const token = await keyStore.loadSession().catch(() => null);
    if (token === null) return;
    try {
      await accountTransportFor(config.serverUrl).logout(token);
    } catch {
      // Best effort: the session expires on the server by itself; the user still leaves this device.
      logger.warn('could not end the account session on the server');
    }
  }

  /**
   * Stop syncing and forget keys, session and configuration (shared by disableSync, logout and deleteAccount).
   * An account session is ended on the server first, except after the account was deleted (`endSession: false`).
   */
  async function disableLocally(endSession = true): Promise<void> {
    const config = configStore.load();
    const keys = activeKeys;
    if (endSession) await endAccountSession(config);
    await detach();
    try {
      // The keys go first: if removing them fails, the configuration stays, sync keeps running
      // and the user can try again. The other order would leave keys that nothing refers to.
      await keyStore.clear();
    } catch (error) {
      if (config !== null && keys !== null) await attach(config, keys);
      throw error;
    }
    configStore.clear();
    setStatus(DISABLED);
  }

  /** Leave the space on this device, optionally deleting the local data (sync is off by then). */
  async function leaveDevice(leave: LeaveOptions | undefined): Promise<void> {
    await disableLocally();
    if (leave?.clearLocalData === true) await clearLocalData();
  }

  /** Records not on the server yet: the engine's outbox rule, read from the stored configuration. */
  async function countUnpushed(): Promise<number> {
    const config = configStore.load();
    if (config === null) return 0;
    const full = config.fullPushFrom !== null;
    const from = config.fullPushFrom ?? config.lastPushedHlc;
    // Exclusive: the record at the watermark itself is already on the server.
    const since = await queries.recordsSince(store.repo, from);
    return since.filter((record) => !isZeroHlc(record.hlc) && (full || record.deviceId === store.deviceId)).length;
  }

  async function resume(): Promise<void> {
    if (engine !== null) return;
    const config = configStore.load();
    if (config === null) {
      setStatus(DISABLED);
      return;
    }
    const keys = await keyStore.load();
    if (keys === null || keys.spaceId !== config.spaceId) {
      logger.warn('sync is configured but the keys are missing');
      setStatus({
        ...DISABLED,
        state: SyncState.Error,
        lastSyncAt: config.lastSyncAt,
        lastError: SyncErrorCode.KeysMissing,
      });
      return;
    }
    await attach(config, keys);
  }

  return {
    start() {
      starting ??= resume().finally(() => {
        starting = null;
      });
      return starting;
    },
    async stop() {
      // A start in progress would attach an engine after this call; let it finish, then stop that engine.
      await starting?.catch(() => undefined);
      await detach();
      setStatus(DISABLED);
    },
    getStatus: () => status,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSettings() {
      const config = configStore.load();
      if (config === null) return null;
      return {
        serverUrl: config.serverUrl,
        spaceIdPrefix: config.spaceId.slice(0, 8),
        lastSyncAt: config.lastSyncAt,
        mode: config.mode,
        accountEmail: config.accountEmail,
      };
    },
    enableSync: (input) =>
      establish(input, async (transport) => {
        try {
          return await transport.putSpace();
        } catch (error) {
          if (error instanceof SyncHttpError && error.code === ServerErrorCode.PassphraseSpacesDisabled)
            throw new PassphraseSpacesDisabledError();
          throw error;
        }
      }),
    register: registerAccount,
    login: loginAccount,
    resetPassword: resetAccountPassword,
    async logout(leave) {
      requireAccountConfig();
      await leaveDevice(leave);
      logger.info('logged out and sync disabled');
    },
    deleteAccount: (input) => deleteAccountOfDevice(input.password),
    changePassword: changeAccountPassword,
    revealRecoveryCode: revealCode,
    upgradeToAccount: upgrade,
    verifyEmail: verifyEmailLink,
    accountInfo: readAccountInfo,
    resendVerification: () =>
      withStoredSession((transport, token) => transport.resendVerification(token, options.locale?.())),
    changeEmail: changeEmailOfAccount,
    listSessions: () => withStoredSession((transport, token) => transport.listSessions(token)),
    revokeSession: (id) => withStoredSession((transport, token) => transport.revokeSession(token, id)),
    renewSession: renewAccountSession,
    async joinSync(input) {
      await establish(input, async (transport) => {
        try {
          const { epoch } = await transport.pull(0, 1);
          return { created: false, epoch };
        } catch (error) {
          if (error instanceof SyncHttpError && error.code === ServerErrorCode.NotFound)
            throw new SpaceNotFoundError();
          throw error;
        }
      });
    },
    async disableSync(leave) {
      await leaveDevice(leave);
      logger.info('sync disabled');
    },
    countUnpushedRecords: countUnpushed,
    async deleteRemoteData() {
      const config = configStore.load();
      const keys = activeKeys ?? (await keyStore.load());
      if (config === null || keys === null) throw new SyncNotEnabledError();
      await detach();
      try {
        await transportFor(config.serverUrl, keys).deleteSpace();
      } catch (error) {
        // Already gone counts as deleted; any other failure leaves sync running as before.
        if (!(error instanceof SyncHttpError && error.status === 404)) {
          await attach(config, keys);
          throw error;
        }
      }
      await endAccountSession(config);
      await keyStore.clear();
      configStore.clear();
      setStatus(DISABLED);
      logger.info('remote data deleted and sync disabled', { space: keys.spaceId.slice(0, 8) });
    },
    syncNow: async (syncOptions) => {
      if (engine === null) return { ok: false, error: null, state: SyncState.Disabled };
      return engine.syncNow(syncOptions);
    },
    reuploadSpace: async () => {
      if (engine === null) return { ok: false, error: null, state: SyncState.Disabled };
      const outcome = await engine.reuploadSpace();
      if (!outcome.ok && outcome.error === SyncErrorCode.PassphraseSpacesDisabled)
        throw new PassphraseSpacesDisabledError();
      return outcome;
    },
    notifyLocalChange: () => engine?.notifyLocalChange(),
    requestFullPush() {
      if (engine !== null) {
        engine.requestFullPush();
        return;
      }
      // No engine is running (it was stopped, or its keys are missing): record the request in the
      // configuration so that the next engine starts with a full push.
      markFullPushPending(configStore);
    },
  };
}
